import { InfiniteData, QueryClient } from '@tanstack/react-query';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';

/**
 * useQueryPlanningsDeliveriesのキャッシュを管理するクラス
 */
export class PlanningsDeliveriesCache {
  constructor(private queryClient: QueryClient) {}

  private readonly cacheKey = 'useQueryPlanningsDeliveries';

  updateDeliveries(deliveries: PlanningsDeliveryEntity[]): void {
    const deliveriesCache = this.queryClient.getQueriesData({ queryKey: [this.cacheKey] });
    deliveriesCache.forEach(([queryCacheKey, cacheData]) => {
      if (cacheData && typeof cacheData === 'object' && 'pages' in cacheData) {
        const paginatedData = cacheData as InfiniteData<{
          ids: number[],
          data: PlanningsDeliveryEntity[],
          total_pages_count: number
        }>;
        if (paginatedData.pages.length > 0) {
          const updatedPages = paginatedData.pages.map((page) => {
            const filtered = deliveries.filter((it) => page.ids.includes(it.id));
            if (filtered.length === 0) return page;

            const newData = page.data.map((it) => {
              const newDelivery = filtered.find((deli) => deli.id === it.id);
              return newDelivery || it;
            });
            return { ...page, data: newData };
          });
          this.queryClient.setQueryData(queryCacheKey, { ...paginatedData, pages: updatedPages });
        }
      }
    });
  }

  removeDeliveryById(deliveryId: number): void {
    const deliveriesCache = this.queryClient.getQueriesData({ queryKey: [this.cacheKey] });
    deliveriesCache.forEach(([queryCacheKey, cacheData]) => {
      if (cacheData && typeof cacheData === 'object' && 'pages' in cacheData) {
        const paginatedData = cacheData as InfiniteData<{
          ids: number[],
          data: PlanningsDeliveryEntity[],
          total_pages_count: number
        }>;
        if (paginatedData.pages.length > 0) {
          const updatedPages = paginatedData.pages.map((page) => {
            const newIds = page.ids.filter((it) => it !== deliveryId);
            const newData = page.data.filter((it) => it.id !== deliveryId);
            return { ids: newIds, data: newData, total_pages_count: page.total_pages_count };
          });

          this.queryClient.setQueryData(queryCacheKey, {
            ...paginatedData,
            pages: updatedPages,
          });
        }
      }
    });
  }

  addDeliveriesByDateRange(deliveries: PlanningsDeliveryEntity[]): void {
    const deliveryCache = this.queryClient.getQueriesData({ queryKey: [this.cacheKey] });
    deliveryCache.forEach(([queryCacheKey, cacheData]) => {
      const queryStartDate = new Date(queryCacheKey[1] as string);
      const queryEndDate = new Date(queryCacheKey[2] as string);
      queryStartDate.setHours(0, 0, 0, 0);
      queryEndDate.setHours(23, 59, 59, 999);

      // startAtが期間内にある配送データをフィルタリング
      const filteredDeliveries = deliveries.filter((delivery) => {
        if (!delivery.startAt) return false;
        const deliveryStartAt = new Date(delivery.startAt);
        return deliveryStartAt >= queryStartDate && deliveryStartAt <= queryEndDate;
      });

      // 既存のキャッシュデータに新しい配送データを追加
      if (cacheData && typeof cacheData === 'object' && 'pages' in cacheData && filteredDeliveries.length > 0) {
        const paginatedData = cacheData as InfiniteData<{
          ids: number[];
          total_pages_count: number;
          data: PlanningsDeliveryEntity[];
        }>;
        const updatedPages = [...paginatedData.pages];

        // 最後のページに新しい配送データを追加
        if (updatedPages.length > 0) {
          const lastPageIndex = updatedPages.length - 1;
          const lastPage = updatedPages[lastPageIndex];
          const filteredIds = filteredDeliveries.map((delivery) => delivery.id);

          updatedPages[lastPageIndex] = {
            ...lastPage,
            ids: [...lastPage.ids, ...filteredIds],
            data: [...lastPage.data, ...filteredDeliveries]
          };
        } else {
          // ページが存在しない場合は新しいページを作成
          const filteredIds = filteredDeliveries.map((delivery) => delivery.id);
          updatedPages.push({
            ids: filteredIds,
            data: filteredDeliveries,
            total_pages_count: 1
          });
        }

        this.queryClient.setQueryData(queryCacheKey, {
          ...paginatedData,
          pages: updatedPages
        });
      }
    });
  }

  removeOrderIds(orderIds: number[]): void {
    const deliveryCache = this.queryClient.getQueriesData({ queryKey: [this.cacheKey] });

    deliveryCache.forEach(([queryCacheKey, cacheData]) => {
      if (cacheData && typeof cacheData === 'object' && 'pages' in cacheData) {
        const paginatedData = cacheData as InfiniteData<{
          ids: number[];
          total_pages_count: number;
          data: PlanningsDeliveryEntity[];
        }>;
        const newPages = paginatedData.pages.map((page) => {
          const data = page.data.map((dt) => ({
            ...dt,
            operations: dt.operations.filter((it) => !orderIds.includes(it.orderId)),
            orderAssignments: dt.orderAssignments.filter((it) => !orderIds.includes(it.order_id)),
          })) as PlanningsDeliveryEntity[];
          return {
            ...page,
            data,
          };
        });
        this.queryClient.setQueryData(queryCacheKey, { ...paginatedData, pages: newPages });
      }
    });
  }
}
